/**
 * SavageTech Widget Service
 *
 * Manages the lifecycle and initialization of the Savage widget.
 */

import { authorizeSavageVendor } from './savage-api.service';
import { defaultVendorTheme } from './savage-widget-theme';

declare global {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface Window {
        Savage?: {
            init: (config: ISavageWidgetConfig) => void;
        };
    }
}

interface ISavagePubSubCredentials {
    token: string;
    baseUrl: string;
    url: string;
}

interface ISavageWidgetConfig {
    container: HTMLElement;
    credentials: {
        vendorId: string;
        jwt: string;
        pubsub: ISavagePubSubCredentials;
    };
    config?: {
        language?: string;
        [key: string]: any;
    };
}

/**
 * Loads the Savage proxy widget script
 *
 * @returns Promise that resolves when the script is loaded.
 */
export function loadSavageScript(): Promise<void> {
    return new Promise((resolve, reject) => {
        if (window.Savage) {
            resolve();
            return;
        }

        // Check if script tag already exists
        const existingScript = document.querySelector(
            'script[src*="savage.js"]',
        );
        if (existingScript) {
            existingScript.addEventListener('load', () => resolve());
            existingScript.addEventListener('error', reject);
            return;
        }

        // Create and append script tag
        const script = document.createElement('script');
        script.src = 'https://proxy.rc.savagebet.gg/savage.js';
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () =>
            reject(new Error('Failed to load Savage widget script'));
        document.head.appendChild(script);
    });
}

/**
 * Initializes the Savage widget for a specific player.
 *
 * @param container - DOM element where the widget will be rendered.
 * @param playerId - Unique player identifier.
 * @param currency - 3-letter currency code (e.g "USD").
 * @param language - Optional language code (e.g: "en", "pl").
 * @returns Promise that resolves when the widget is initialized.
 */
export async function initializeSavageWidget(
    container: HTMLElement,
    playerId: string,
    currency: string,
    language: string = 'en_US',
): Promise<void> {
    try {
        // 1. Load the Savage script
        await loadSavageScript();

        // 2. Authorize the player and get credentials
        const auth = await authorizeSavageVendor(playerId, currency);

        // 3. Initialize the widget
        if (!window.Savage) {
            throw new Error(
                'Savage widget script loaded but window.Savage is not available',
            );
        }

        window.Savage.init({
            container,
            credentials: {
                vendorId: auth.vendorId,
                jwt: auth.jwt,
                pubsub: auth.pubsub,
            },
            config: {
                language,
                styles: defaultVendorTheme,
            },
        });
    } catch (error) {
        console.error('Failed to initialize Savage widget:', error);
        throw error;
    }
}
